import { Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { VehiclesService } from '../../../services/vehicles.service';
import { Vehicle } from '../../../models/Vehicle';
import { ServiceOrdersService } from '../../../services/service-orders.service';
import { ServiceOrder } from '../../../models/ServiceOrder';

export interface VehicleSelection {
  id: number;
  placa: string;
  modelo: string;
  marca: string;
  ano: number;
  cor: string;
  chassi: string;
}

@Component({
  selector: 'app-vehicle-selection-modal',
  templateUrl: './vehicle-selection-modal.component.html',
  styleUrls: ['./vehicle-selection-modal.component.css']
})
export class VehicleSelectionModalComponent implements OnInit, OnDestroy {
  @Output() vehicleSelected = new EventEmitter<VehicleSelection>();
  @Output() closeModal = new EventEmitter<void>();

  showModal: boolean = false;
  searchTerm: string = '';
  vehicles: VehicleSelection[] = [];
  filteredVehicles: VehicleSelection[] = [];
  selectedVehicle: VehicleSelection | null = null;
  loading: boolean = false;

  checkingExistingServiceOrders: boolean = false;
  showExistingServiceOrdersModal: boolean = false;
  existingServiceOrders: ServiceOrder[] = [];
  blockedVehicle: VehicleSelection | null = null;
  private lastCheckVehicleId: number | null = null;
  
  // Paginação
  currentPage: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;

  // Debounce para pesquisa na API
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private vehiclesService: VehiclesService,
    private serviceOrdersService: ServiceOrdersService
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(term => {
      this.currentPage = 1;
      this.selectedVehicle = null;
      this.loadVehicles(term);
    });

    this.loadVehicles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Helper para usar Math.min no template
   */
  get Math() {
    return Math;
  }

  /**
   * Carrega veículos da API com filtro de pesquisa opcional
   */
  loadVehicles(search?: string): void {
    this.loading = true;
    const term = search?.trim() || this.searchTerm?.trim() || undefined;
    const filters = term ? { search: term } : undefined;
    this.vehiclesService.getAllPaginated(this.currentPage, this.pageSize, filters).subscribe({
      next: (response) => {
        this.vehicles = response.items.map(v => this.mapVehicleToSelection(v));
        this.filteredVehicles = [...this.vehicles];
        this.totalCount = response.totalItems ?? this.vehicles.length;
        this.totalPages = response.totalPages ?? 1;
        this.currentPage = response.page ?? this.currentPage;
        this.pageSize = response.pageSize ?? this.pageSize;
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar veículos:', err);
        this.loading = false;
        this.vehicles = [];
        this.filteredVehicles = [];
      }
    });
  }

  /**
   * Mapeia Vehicle para VehicleSelection
   */
  private mapVehicleToSelection(vehicle: Vehicle): VehicleSelection {
    return {
      id: vehicle.id,
      placa: vehicle.plateNumber,
      modelo: vehicle.model,
      marca: vehicle.brand,
      ano: vehicle.modelYear,
      cor: vehicle.color ?? 'N/A',
      chassi: vehicle.chassisNumber
    };
  }

  open(): void {
    this.showModal = true;
    this.searchTerm = '';
    this.selectedVehicle = null;
    this.resetExistingOrdersModal();
    this.currentPage = 1;
    this.loadVehicles();
  }

  close(): void {
    this.showModal = false;
    this.resetExistingOrdersModal();
    this.closeModal.emit();
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  selectVehicle(vehicle: VehicleSelection): void {
    this.selectedVehicle = vehicle;
    this.checkExistingServiceOrdersForVehicle(vehicle);
  }

  confirmSelection(): void {
    if (this.selectedVehicle && !this.checkingExistingServiceOrders) {
      this.vehicleSelected.emit(this.selectedVehicle);
      this.close();
    }
  }

  closeExistingServiceOrdersModal(): void {
    this.showExistingServiceOrdersModal = false;
    this.existingServiceOrders = [];
    this.blockedVehicle = null;
  }

  private resetExistingOrdersModal(): void {
    this.checkingExistingServiceOrders = false;
    this.showExistingServiceOrdersModal = false;
    this.existingServiceOrders = [];
    this.blockedVehicle = null;
    this.lastCheckVehicleId = null;
  }

  private checkExistingServiceOrdersForVehicle(vehicle: VehicleSelection): void {
    if (!vehicle?.id) return;

    const vehicleId = vehicle.id;
    this.lastCheckVehicleId = vehicleId;
    this.checkingExistingServiceOrders = true;

    this.serviceOrdersService.getByVehicleId(vehicleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orders) => {
          if (this.lastCheckVehicleId !== vehicleId) return;

          const OpenOrders = orders.filter(order => order.status !== 'Encerrada');
          this.checkingExistingServiceOrders = false;

          if (OpenOrders.length > 0) {
            this.blockedVehicle = vehicle;
            this.existingServiceOrders = OpenOrders;
            this.showExistingServiceOrdersModal = true;
            this.selectedVehicle = null;
          }
        },
        error: (err) => {
          if (this.lastCheckVehicleId !== vehicleId) return;
          console.error('Erro ao verificar ordens de serviço do veículo:', err);
          this.checkingExistingServiceOrders = false;
        }
      });
  }

  /**
   * Vai para a próxima página
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.selectedVehicle = null;
      this.loadVehicles();
    }
  }

  /**
   * Vai para a página anterior
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.selectedVehicle = null;
      this.loadVehicles();
    }
  }

  /**
   * Vai para uma página específica
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.selectedVehicle = null;
      this.loadVehicles();
    }
  }

  /**
   * Gera array de números de página para exibição
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    
    if (this.totalPages <= maxPagesToShow) {
      // Mostra todas as páginas se forem poucas
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Lógica para mostrar páginas com reticências
      const halfWindow = Math.floor(maxPagesToShow / 2);
      let startPage = Math.max(1, this.currentPage - halfWindow);
      let endPage = Math.min(this.totalPages, this.currentPage + halfWindow);
      
      // Ajusta se estiver no início
      if (this.currentPage <= halfWindow) {
        endPage = maxPagesToShow;
      }
      
      // Ajusta se estiver no final
      if (this.currentPage >= this.totalPages - halfWindow) {
        startPage = this.totalPages - maxPagesToShow + 1;
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }
}

